import { del, get, post, put } from "../../../core/api/client";
import { API_ENDPOINTS } from "../../../core/api/endpoints";

const sanitizeLimit = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return 20;
  return Math.min(100, Math.max(1, Math.trunc(numeric)));
};

const sanitizeOffset = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return 0;
  return Math.max(0, Math.trunc(numeric));
};

const toNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

export const createReferral = async (payload) => {
  return post(API_ENDPOINTS.REFERRALS.CREATE, payload);
};

export const getReferralsByClient = async (
  clientId,
  params = {},
) => {
  const { limit = 20, offset = 0 } = params;
  const safeLimit = sanitizeLimit(limit);
  const safeOffset = sanitizeOffset(offset);

  return get(API_ENDPOINTS.REFERRALS.LIST_BY_CLIENT(clientId, safeLimit, safeOffset));
};

export const getReferralById = async (id) => {
  return get(API_ENDPOINTS.REFERRALS.GET_BY_ID(id));
};

export const updateReferralById = async (id, payload) => {
  return put(API_ENDPOINTS.REFERRALS.UPDATE_BY_ID(id), payload);
};

export const deleteReferralById = async (id) => {
  return del(API_ENDPOINTS.REFERRALS.DELETE_BY_ID(id));
};

export const getClientReferralLink = async (clientId) => {
  const response = await get(`${API_ENDPOINTS.CLIENTS.LIST}?limit=20&offset=0`);

  if (response?.error) {
    return response;
  }

  const clients = Array.isArray(response?.clients) ? response.clients : [];
  const targetClient =
    clients.find((item) => Number(item.id) === Number(clientId)) || null;

  return {
    client: targetClient,
    referralLink: targetClient?.client_referral_link || "",
  };
};

export const getReferralDashboardData = async (
  clientId,
  params = {},
) => {
  const { limit = 20, offset = 0 } = params;

  const [referralLinkResponse, referralsResponse] = await Promise.all([
    getClientReferralLink(clientId),
    getReferralsByClient(clientId, { limit, offset }),
  ]);

  if (referralLinkResponse?.error && referralsResponse?.error) {
    return {
      error: {
        message:
          referralsResponse.error.message ||
          referralLinkResponse.error.message ||
          "Unable to load referral data.",
      },
    };
  }

  const referrals = Array.isArray(referralsResponse?.referrals)
    ? referralsResponse.referrals
    : [];

  const totalReferrals = toNumber(referralsResponse?.total, referrals.length);
  const totalEarningsNumber = referrals.reduce(
    (sum, item) => sum + toNumber(item?.referral_bonus, 0),
    0,
  );

  const activeReferrals = referrals.filter((item) => {
    const status = String(item?.referral_status || "").toLowerCase();
    if (status) return status === "active";
    return Boolean(item?.invoice_id);
  }).length;

  return {
    referralLink: referralLinkResponse?.referralLink || "",
    client: referralLinkResponse?.client || null,
    referrals,
    total: totalReferrals,
    stats: {
      totalClicks: String(totalReferrals),
      totalSignups: String(totalReferrals),
      activeReferrals: String(activeReferrals),
      totalEarnings: `$${totalEarningsNumber.toFixed(2)}`,
    },
    referralsError: referralsResponse?.error?.message || "",
    clientError: referralLinkResponse?.error?.message || "",
  };
};
